import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { newPurchase, unlockSeats } from '@/actions/purchase';
import { quickPurchaseBackToSearchTracker } from 'metrics/user-analytics/purchase';
import useSearch from 'hooks/store/useSearch';
import useLoyaltyPrograms from '../../../loyalty/context/useLoyaltyPrograms';
import NewQuickPurchase from './NewQuickPurchase';
import buildQuickPurchaseExitSearchUrl from './buildQuickPurchaseExitSearchUrl';
import { cloneSelectedSeatReferences } from '../../../utils/purchase/seatReferenceTransport';

/**
 * Reads quick purchase query params using URLSearchParams so encoded URL values
 * from buildQuickPurchasePath are decoded before being reused.
 * @param {string} search - Location search string.
 * @returns {object} Quick purchase query params.
 */
const getQuickPurchaseQueryParams = (search) => {
  const params = new URLSearchParams(search);
  return {
    seenPrice: params.get('seenPrice') || undefined,
    seats: params.get('seats') || undefined,
    coupon: params.get('coupon') || undefined,
  };
};

/**
 * Builds the pre-selected seats payload expected by newPurchase.
 * @param {string} seats - Comma-separated selected seat numbers.
 * @returns {object|undefined} Pre-selected seats payload.
 */
export const buildPreSelectedSeats = (seats, selectedSeatReferences) => {
  const departureSeats =
    cloneSelectedSeatReferences(selectedSeatReferences) ||
    seats
      ?.split(',')
      .filter(Boolean)
      .map((number) => ({ number }));

  if (!departureSeats?.length) return undefined;

  return {
    preSelectedSeats: {
      departure: departureSeats,
    },
  };
};

/**
 * Maps quick purchase route state to props.
 * @param {object} state - Redux state.
 * @param {object} ownProps - Router props.
 * @returns {object} Component props.
 */
export const mapStateToProps = (state, ownProps) => {
  const { seats } = getQuickPurchaseQueryParams(ownProps.location.search);
  return {
    purchase: state.purchase.toJS(),
    passengersParam: ownProps.match.params.passengers || 'A1',
    seatsParam: seats || null,
    selectedSeatReferences: cloneSelectedSeatReferences(
      ownProps.location.state?.selectedSeatReferences,
    ),
  };
};

/**
 * Maps quick purchase creation dispatchers.
 * @param {Function} dispatch - Redux dispatch.
 * @param {object} ownProps - Router props.
 * @returns {object} Dispatch props.
 */
export const mapDispatchToProps = (dispatch, ownProps) => ({
  /**
   * Creates the quick purchase from route and query params.
   * @returns {Promise<string|undefined>} Resolved purchase token.
   */
  createPurchase: () => {
    const { location, match } = ownProps;
    const { departureId, returnId, passengers = 'A1' } = match.params;
    const { seenPrice, seats, coupon } = getQuickPurchaseQueryParams(location.search);
    const preSelectedSeats = buildPreSelectedSeats(seats, location.state?.selectedSeatReferences);
    return dispatch(
      newPurchase(
        departureId,
        returnId,
        passengers,
        seenPrice,
        undefined,
        false,
        {},
        false,
        '',
        undefined,
        preSelectedSeats,
        true,
        coupon,
        { purchaseSurface: 'fullPage' },
      ),
    );
  },
  /**
   * Releases seats for an abandoned quick purchase create.
   * @param {string} token - Purchase token to unlock.
   * @returns {Promise<void>}
   */
  unlockPurchaseSeats: (token) =>
    unlockSeats({ purchaseToken: token, forceUnlock: true, shouldUpdateState: false, dispatch }),
  /**
   * Replaces the current route after quick purchase creation.
   * @param {string} route - Next quick checkout route.
   * @returns {void}
   */
  replaceUrl: (route) =>
    ownProps.history.replace(route, {
      selectedSeatReferences: cloneSelectedSeatReferences(
        ownProps.location.state?.selectedSeatReferences,
      ),
    }),
});

const ConnectedNewQuickPurchase = connect(mapStateToProps, mapDispatchToProps)(NewQuickPurchase);

/**
 * NewQuickPurchaseContainer component.
 * Creates a quick purchase and returns to schedules when a wallet session starts.
 * @param {object} props - history, location, match.
 * @param {object} props.history - React Router history object.
 * @param {object} props.location - React Router location object.
 * @param {object} props.match - React Router match object.
 * @returns {React.ReactElement}
 */
const NewQuickPurchaseContainer = ({ history, location, match }) => {
  const { userIsLoggedInWithAnyLoyaltyProgram } = useLoyaltyPrograms();
  const search = useSearch();
  const features = useSelector((state) => state.whitelabelConfig.features);
  const passengersParam = match.params.passengers || 'A1';
  const hasExitedRef = useRef(false);

  useEffect(() => {
    if (!userIsLoggedInWithAnyLoyaltyProgram || hasExitedRef.current) {
      return;
    }

    const exitUrl = buildQuickPurchaseExitSearchUrl({ search, passengersParam, features });

    if (!exitUrl) {
      return;
    }

    hasExitedRef.current = true;
    quickPurchaseBackToSearchTracker();
    history.replace(exitUrl);
  }, [features, history, passengersParam, search, userIsLoggedInWithAnyLoyaltyProgram]);

  return <ConnectedNewQuickPurchase history={history} location={location} match={match} />;
};

NewQuickPurchaseContainer.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default NewQuickPurchaseContainer;
